import { create } from "zustand";
import { AlgoConfig } from "./defaults";
import { db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";

interface ConfigsState {
  configs: AlgoConfig[];
  isLoading: boolean;
  hasError: boolean;
  loadConfigs: (userId: string) => Promise<void>;
  addConfig: (userId: string, config: AlgoConfig) => Promise<void>;
  updateConfig: (userId: string, config: AlgoConfig) => Promise<void>;
  deleteConfig: (userId: string, configId: string) => Promise<void>;
}

export const useConfigsStore = create<ConfigsState>((set, get) => ({
  configs: [],
  isLoading: false,
  hasError: false,

  loadConfigs: async (userId: string) => {
    set({ isLoading: true, hasError: false });
    try {
      const colRef = collection(db, "users", userId, "algoConfigs");
      const snapshot = await getDocs(colRef);
      const configs: AlgoConfig[] = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as AlgoConfig,
      );
      set({ configs, isLoading: false });
    } catch (e) {
      set({ hasError: true, isLoading: false });
    }
  },

  addConfig: async (userId: string, config: AlgoConfig) => {
    try {
      const docRef = doc(db, "users", userId, "algoConfigs", config.id);
      await setDoc(docRef, { ...config });
      set({ configs: [...get().configs, config] });
    } catch (e) {
      set({ hasError: true });
    }
  },

  updateConfig: async (userId: string, config: AlgoConfig) => {
    try {
      const docRef = doc(db, "users", userId, "algoConfigs", config.id);
      await updateDoc(docRef, { ...config });
      set({
        configs: get().configs.map((c) => (c.id === config.id ? config : c)),
      });
    } catch (e) {
      set({ hasError: true });
    }
  },

  deleteConfig: async (userId: string, configId: string) => {
    try {
      const docRef = doc(db, "users", userId, "algoConfigs", configId);
      await deleteDoc(docRef);
      set({ configs: get().configs.filter((c) => c.id !== configId) });
    } catch (e) {
      set({ hasError: true });
    }
  },
}));
